/**
 * BRO-367: Shared client display helpers for BSUID/username support.
 *
 * Import these everywhere instead of per-file ternary chains.
 * Fallback chain: display_name → full_name → name → @username → phone → BSUID → 'Unknown'
 */

interface ClientIdentifiers {
    /** BRO-1362: title + name, placeholder-aware. Preferred over full_name. */
    display_name?: string | null;
    full_name?: string | null;
    name?: string | null;
    phone?: string | null;
    whatsapp_user_id?: string | null;
    whatsapp_username?: string | null;
}

/**
 * Best display name for a client. Used in headers, lists, notifications.
 */
export function clientDisplayName(client: ClientIdentifiers): string {
    // BRO-1362: display_name first. It is the only form that carries the contact's title
    // and that knows the stored name may be one the system invented (Client::
    // getDisplayNameAttribute). full_name is title-less and, for a contact stored as
    // title + surname with no first name, renders as a bare surname with a leading space.
    // Callers that want INITIALS must not use this: take them from first/last directly,
    // or "Mr Redwood" initialises as "MR".
    return client.display_name
        || client.full_name
        || client.name
        || (client.whatsapp_username ? `@${client.whatsapp_username}` : null)
        || client.phone
        || client.whatsapp_user_id
        || 'Unknown';
}

/**
 * Secondary identifier line. Shows phone and @username combined when both exist.
 * e.g. "+447714681600 · @alanfuller" or just "+447714681600" or just "@alanfuller"
 * Returns null if no identifier available.
 */
export function clientContactLine(client: ClientIdentifiers): string | null {
    const parts: string[] = [];
    if (client.phone) parts.push(client.phone);
    if (client.whatsapp_username) parts.push(`@${client.whatsapp_username}`);
    if (parts.length > 0) return parts.join(' · ');
    if (client.whatsapp_user_id) return client.whatsapp_user_id;
    return null;
}

/**
 * BRO-1567: the same line, masked PART BY PART.
 *
 * THE DEFECT THIS REPLACES, found by Quality. Callers did
 * `line.startsWith('@') ? maskHandle(...) : maskPhone(line)`. When BOTH a phone and a handle
 * exist, `clientContactLine` returns "phone · @handle", which does not start with "@", so the
 * WHOLE line went through maskPhone. maskPhone strips non-digits and keeps the last three -
 * and for a handle containing digits, those last three digits are the HANDLE'S. A mask
 * designed to reveal three digits of a phone number was revealing part of a username instead,
 * and handles are masked wholesale precisely because their first characters leak.
 *
 * Masking each part with its own mask is the only thing that composes correctly. Doing it
 * HERE rather than at four call sites is the point: the line is BUILT here, so the masked
 * form belongs here too, and the four callers cannot disagree about it.
 *
 * Not in privacyMask.ts, deliberately: that module is mirrored byte-for-byte in PHP and this
 * has no PHP counterpart, so adding it there would put the two implementations out of step
 * for no reason.
 */
export function maskedClientContactLine(
    client: ClientIdentifiers,
    maskPhone: (value: string | null | undefined) => string,
    maskHandle: (value: string | null | undefined) => string,
): string | null {
    const parts: string[] = [];

    if (client.phone) parts.push(maskPhone(client.phone));
    if (client.whatsapp_username) parts.push(`@${maskHandle(client.whatsapp_username)}`);
    if (parts.length > 0) return parts.join(' · ');

    // A wa_id is the phone number without its plus, so it takes the phone mask.
    if (client.whatsapp_user_id) return maskPhone(client.whatsapp_user_id);

    return null;
}

export type { ClientIdentifiers };
